/**
 * Approved Suppliers API client — Session 3.10.
 */

import type { SupplierAnswers } from '@jeanieiq/types';
import type { SupplierVisitScores } from '@jeanieiq/types';
import { apiDownload, apiFetch, apiUrl } from './api';
import { getAccessToken } from './auth';

export type SupplierStatus = 'approved' | 'pending_approval' | 'suspended' | 'removed';
export type EffectiveStatus = SupplierStatus | 'review_due';

export interface SupplierDocument {
  id: string;
  type: string;
  filename: string;
  s3Key: string;
  mimeType: string | null;
  sizeBytes: number;
  expiryDate: string | null;
  uploadedAt: string;
  uploadedById: string | null;
}

export interface SupplierPublic {
  id: string;
  /** Corrective actions about this supplier, cancelled ones left out. */
  nonConformities: { total: number; open: number };
  questionnaireSentAt: string | null;
  questionnaireReturnedAt: string | null;
  lastAssessedOn: string | null;
  accountRef: string | null;
  companyName: string;
  sinceYear: number | null;
  categories: string[];
  isSubcontractor: boolean;
  isCisSubcontractor: boolean;
  companiesHouseNumber: string | null;
  vatNumber: string | null;
  status: SupplierStatus;
  effectiveStatus: EffectiveStatus;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  accountsEmail: string | null;
  accountsPhone: string | null;
  /** Which section — a value from the tenant's own list. NULL = none set. */
  supplierType: string | null;
  website: string | null;
  /** Entered on the onboarding card; null until it is. */
  registeredAddress: SupplierAddress | null;
  /** 'net' (from the end of the invoice month) or 'from_invoice'; null until set. */
  paymentTermsBasis: string | null;
  /** NULL = not agreed yet. A default is not an agreement. */
  paymentTermsDays: number | null;
  /** How often this supplier is reviewed, in MONTHS. Always set; 12 default. */
  reviewIntervalMonths: number;
  currency: string;
  /** SUPPLIER-CREDIT-CHECK-001 (Shane, 2026-08-19: "so you can see what there
   *  level is"). A supplier's standing is a supply risk in the same way a
   *  customer's is a payment risk. A check past its next-due is not current. */
  creditAgency: string | null;
  creditScore: number | null;
  creditCheckedOn: string | null;
  creditNextCheckDue: string | null;
  bankDetailsMasked: string | null;
  bankDetailsCheckedAt: string | null;
  bankDetailsCheckedBy: string | null;
  cisVerificationRef: string | null;
  cisVerificationDate: string | null;
  cisDeductionRate: number | null;
  publicLiabilityInsurer: string | null;
  publicLiabilityValue: string | null;
  publicLiabilityExpiry: string | null;
  employersLiabilityInsurer: string | null;
  employersLiabilityValue: string | null;
  employersLiabilityExpiry: string | null;
  professionalIndemnityInsurer: string | null;
  professionalIndemnityValue: string | null;
  professionalIndemnityExpiry: string | null;
  qualityRating: number | null;
  deliveryRating: number | null;
  onTimePct: number | null;
  totalSpend: string | null;
  lastOrderDate: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  approvalNotes: string | null;
  nextReviewDate: string | null;
  notes: string | null;
  /** Real uploaded documents. Empty = none; the panel used to invent three. */
  documents: SupplierDocument[];
  createdAt: string;
}

export interface SupplierListResult {
  suppliers: SupplierPublic[];
  total: number;
}

export interface SupplierStatsResult {
  approved: number;
  reviewDue: number;
  insuranceExpiring: number;
  cisSubs: number;
}

export interface IsoEvidencePublic {
  id: string;
  clause: string;
  evidenceType: string;
  relatedEntityType: string;
  relatedEntityId: string;
  description: string;
  recordedAt: string;
  /**
   * The record this evidence is about — a performance review's id, for rows
   * filed by a review. Null on evidence that describes nothing else (2026-08-31).
   *
   * Lets the clause 8.4 log link a review to the nonconformity it raised.
   * `relatedEntityId` is the supplier, which cannot say WHICH review a row is,
   * so this is the exact join.
   */
  sourceRecordId: string | null;
}

export interface IsoStatsResult {
  records: number;
  evaluated: number;
  lastExportAt: string | null;
}

export interface IsoListResult {
  evidence: IsoEvidencePublic[];
  stats: IsoStatsResult;
}

/** A supplier's registered address, in the platform's four lines. */
export interface SupplierAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
}

export interface CreateSupplierPayload {
  companyName: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  categories: string[];
  isSubcontractor?: boolean;
  isCisSubcontractor?: boolean;
  companiesHouseNumber?: string | null;
  vatNumber?: string | null;
  supplierType?: string | null;
  website?: string | null;
  registeredAddress?: SupplierAddress | null;
  paymentTermsBasis?: 'net' | 'from_invoice' | null;
  accountsEmail?: string | null;
  accountsPhone?: string | null;
  paymentTermsDays?: number | null;
  reviewIntervalMonths?: number;
  /** SUPPLIER-CREDIT-CHECK-001. Dates are YYYY-MM-DD. */
  creditAgency?: string | null;
  creditScore?: number | null;
  creditCheckedOn?: string | null;
  creditNextCheckDue?: string | null;
  currency?: string;
  notes?: string | null;
}

/**
 * The tenant's own supplier categories — theirs to add to and take off.
 * Shane, 2026-08-21: *"like other sections we can add and delete categories"*.
 */
export interface SupplierCategoryOption {
  value: string;
  label: string;
  /** False for the tenant's own additions, which can be removed outright. */
  builtIn: boolean;
  /** How many suppliers sit in it — shown before anyone removes one. */
  inUse: number;
}

export async function fetchSupplierCategories(
  signal?: AbortSignal,
): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>('/api/suppliers/categories', {
    method: 'GET',
    signal,
  });
}

/** Add a category, or rename one. */
export async function upsertSupplierCategory(input: {
  label: string;
  value?: string;
}): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>('/api/suppliers/categories', {
    method: 'POST',
    body: input,
  });
}

export async function removeSupplierCategory(value: string): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>(
    `/api/suppliers/categories/${encodeURIComponent(value)}`,
    { method: 'DELETE' },
  );
}

/** The tenant's section list — goods supplier, service provider, and any they add. */
export async function fetchSupplierSections(
  signal?: AbortSignal,
): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>('/api/suppliers/sections', { method: 'GET', signal });
}

export async function upsertSupplierSection(input: {
  label: string;
  value?: string;
}): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>('/api/suppliers/sections', {
    method: 'POST',
    body: input,
  });
}

export async function removeSupplierSection(value: string): Promise<SupplierCategoryOption[]> {
  return apiFetch<SupplierCategoryOption[]>(
    `/api/suppliers/sections/${encodeURIComponent(value)}`,
    { method: 'DELETE' },
  );
}

export async function fetchSuppliers(
  opts: { status?: EffectiveStatus | ''; category?: string; search?: string } = {},
  signal?: AbortSignal,
): Promise<SupplierListResult> {
  return apiFetch<SupplierListResult>('/api/suppliers', {
    method: 'GET',
    query: {
      status: opts.status || null,
      category: opts.category || null,
      search: opts.search || null,
    },
    signal,
  });
}

export async function fetchSupplierStats(signal?: AbortSignal): Promise<SupplierStatsResult> {
  return apiFetch<SupplierStatsResult>('/api/suppliers/stats', { method: 'GET', signal });
}

export async function fetchSupplierBadge(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/suppliers/badge', { method: 'GET', signal });
  return data.count;
}

export async function fetchSupplier(id: string, signal?: AbortSignal): Promise<SupplierPublic> {
  return apiFetch<SupplierPublic>(`/api/suppliers/${id}`, { method: 'GET', signal });
}

export async function fetchIsoEvidence(
  opts: { clause?: string } = {},
  signal?: AbortSignal,
): Promise<IsoListResult> {
  return apiFetch<IsoListResult>('/api/suppliers/iso-evidence', {
    method: 'GET',
    query: { clause: opts.clause || null },
    signal,
  });
}

/*
 * Contacts at a supplier (2026-08-31) - the same record the CRM has had since it
 * was built. Shane: *"there will be more than 1 contact with different titles"*.
 */
export interface CompanyMatch {
  id: string;
  companyName: string;
  primaryContactName: string | null;
}

/**
 * Suppliers already on the register that look like this company name. Used by
 * Add supplier to offer attaching the person as a contact instead of creating
 * the company again.
 */
export async function matchSuppliersByCompanyName(
  companyName: string,
  signal?: AbortSignal,
): Promise<CompanyMatch[]> {
  return apiFetch<CompanyMatch[]>('/api/suppliers/match', {
    method: 'GET',
    query: { companyName },
    signal,
  });
}

export interface SupplierContactPublic {
  id: string;
  supplierId: string;
  supplierName: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
}

export interface SupplierContactPayload {
  name: string;
  jobTitle?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
}

export async function fetchContactsForSupplier(
  supplierId: string,
  signal?: AbortSignal,
): Promise<SupplierContactPublic[]> {
  return apiFetch<SupplierContactPublic[]>(`/api/suppliers/${supplierId}/contacts`, {
    method: 'GET',
    signal,
  });
}

export async function addSupplierContact(
  supplierId: string,
  payload: SupplierContactPayload,
): Promise<SupplierContactPublic> {
  return apiFetch<SupplierContactPublic>(`/api/suppliers/${supplierId}/contacts`, {
    method: 'POST',
    body: payload,
  });
}

export async function updateSupplierContact(
  supplierId: string,
  contactId: string,
  payload: SupplierContactPayload,
): Promise<void> {
  await apiFetch<unknown>(`/api/suppliers/${supplierId}/contacts/${contactId}`, {
    method: 'PUT',
    body: payload,
  });
}

export async function removeSupplierContact(
  supplierId: string,
  contactId: string,
): Promise<void> {
  await apiFetch<unknown>(`/api/suppliers/${supplierId}/contacts/${contactId}`, {
    method: 'DELETE',
  });
}

/*
 * PERFORMANCE REVIEWS (2026-08-31) — ISO 8.4.1 re-evaluation.
 *
 * Shane: *"when we do a performace review it all logs there and gives a 1-5 star
 * rating with notes to why"*. Before this the Performance tab read five supplier
 * columns that no route accepted and no service computed, so the only ratings on
 * screen anywhere were seed data.
 */
export interface SupplierPerformanceReviewPublic {
  id: string;
  supplierId: string;
  supplierName: string;
  reviewDate: string;
  qualityStars: number;
  deliveryStars: number;
  /** Derived server-side, so no screen has to know a star is worth 20%. */
  qualityPercent: number;
  deliveryPercent: number;
  notes: string;
  /** The 10.2 nonconformity this review raised, where it raised one. */
  capaId: string | null;
  capaTitle: string | null;
  capaStatus: string | null;
  /** Why a below-par score is NOT a nonconformity, where the reviewer said so. */
  ncDeclinedReason: string | null;
  reviewedById: string;
  reviewedByName: string | null;
  createdAt: string;
}

export interface RecordReviewPayload {
  reviewDate: string;
  qualityStars: number;
  deliveryStars: number;
  notes: string;
  /** Raise a new nonconformity from this review. */
  raiseNonConformance?: boolean;
  /** Or attach to one already on the register. */
  capaId?: string | null;
  /** Or say why a below-par score is not one. */
  ncDeclinedReason?: string | null;
  ncSeverity?: 'minor' | 'major' | null;
}

/** One row on the ISO 10.2 register, already raised for this supplier. */
export interface SupplierNonConformancePublic {
  id: string;
  title: string;
  status: string;
  severity: string | null;
  source: string | null;
  raisedOn: string;
}

/**
 * The OPEN nonconformities for a supplier, so a review can point at one rather
 * than raise a duplicate. Shane: *"this can then be slected in the supplier
 * reformance review"*.
 */
export async function fetchSupplierNonConformances(
  supplierId: string,
  signal?: AbortSignal,
): Promise<SupplierNonConformancePublic[]> {
  return apiFetch<SupplierNonConformancePublic[]>(
    `/api/suppliers/${supplierId}/non-conformances`,
    { method: 'GET', signal },
  );
}

export async function fetchSupplierPerformanceReviews(
  signal?: AbortSignal,
): Promise<SupplierPerformanceReviewPublic[]> {
  return apiFetch<SupplierPerformanceReviewPublic[]>('/api/suppliers/performance-reviews', {
    method: 'GET',
    signal,
  });
}

export async function fetchReviewsForSupplier(
  supplierId: string,
  signal?: AbortSignal,
): Promise<SupplierPerformanceReviewPublic[]> {
  return apiFetch<SupplierPerformanceReviewPublic[]>(
    `/api/suppliers/${supplierId}/performance-reviews`,
    { method: 'GET', signal },
  );
}

export async function recordSupplierPerformanceReview(
  supplierId: string,
  payload: RecordReviewPayload,
): Promise<SupplierPerformanceReviewPublic> {
  return apiFetch<SupplierPerformanceReviewPublic>(
    `/api/suppliers/${supplierId}/performance-reviews`,
    { method: 'POST', body: payload },
  );
}

export async function createSupplier(payload: CreateSupplierPayload): Promise<SupplierPublic> {
  return apiFetch<SupplierPublic>('/api/suppliers', { method: 'POST', body: payload });
}

export async function updateSupplier(
  id: string,
  payload: Partial<CreateSupplierPayload>,
): Promise<void> {
  await apiFetch<unknown>(`/api/suppliers/${id}`, { method: 'PATCH', body: payload });
}

/** Bank details for paying the supplier by bank file. The number is stored encrypted. */
export async function setSupplierBankDetails(
  id: string,
  payload: { accountName: string; sortCode: string; accountNumber: string },
): Promise<{ accountNumberLast4: string }> {
  return apiFetch<{ accountNumberLast4: string }>(`/api/suppliers/${id}/bank-details`, { method: 'PUT', body: payload });
}

/** Records that the bank details on file were confirmed with the supplier. */
export async function markSupplierBankDetailsChecked(id: string): Promise<{ checkedAt: string }> {
  return apiFetch<{ checkedAt: string }>(`/api/suppliers/${id}/bank-details/checked`, { method: 'POST' });
}

export async function approveSupplier(
  id: string,
  payload: { approvalNotes?: string; reviewYears?: number } = {},
): Promise<SupplierPublic> {
  return apiFetch<SupplierPublic>(`/api/suppliers/${id}/approve`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function suspendSupplier(id: string, reason?: string): Promise<void> {
  await apiFetch<unknown>(`/api/suppliers/${id}/suspend`, {
    method: 'PATCH',
    body: reason ? { reason } : {},
  });
}

// F032 — Request Review brings the supplier's nextReviewDate forward to now.
// Supplier remains approved/suspended; only the review date moves.
export async function requestSupplierReview(id: string): Promise<SupplierPublic> {
  return apiFetch<SupplierPublic>(`/api/suppliers/${id}/request-review`, {
    method: 'POST',
    body: {},
  });
}

/**
 * POST /api/suppliers/:id/documents (multipart-form).
 *
 * THIS USED TO SEND A FILENAME AND NO FILE. The server then built a
 * realistic-looking S3 key, stored nothing, and returned it — so the checklist
 * ticked "Public Liability insurance document uploaded" against a document that
 * did not exist. Sends the actual file now.
 *
 * apiFetch is JSON-only, so this builds the FormData and runs fetch directly,
 * reusing the Bearer-token + envelope-unwrap conventions — same shape as
 * uploadAttachment in attachmentsApi.ts.
 */
export async function uploadSupplierDocument(
  id: string,
  payload: { file: File; type: string; expiryDate?: string | null },
): Promise<SupplierDocument> {
  const fd = new FormData();
  fd.append('file', payload.file);
  fd.append('type', payload.type);
  if (payload.expiryDate) fd.append('expiryDate', payload.expiryDate);

  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(apiUrl(`/api/suppliers/${id}/documents`), {
    method: 'POST',
    headers,
    body: fd,
  });

  let envelope: unknown = null;
  try {
    envelope = await res.json();
  } catch {
    throw new Error('Upload failed');
  }
  const env = envelope as { success?: boolean; data?: SupplierDocument; error?: { message?: string } };
  if (!res.ok || !env?.success || !env.data) {
    throw new Error(env?.error?.message ?? 'Upload failed');
  }
  return env.data;
}

/** Where to point a link or an iframe to read a stored document back. */
export function supplierDocumentDownloadUrl(supplierId: string, docId: string): string {
  return `/api/suppliers/${supplierId}/documents/${docId}/download`;
}

// ---------------------------------------------------------------------------
// Session 3.18 — Import / Export tab
// ---------------------------------------------------------------------------

export interface SupplierImportPreviewRow {
  rowNumber: number;
  companyName: string;
  category: string;
  cisDeductionRate: number | null;
  effectiveStatus: 'pending_approval';
  nextReviewDate: null;
}

export interface SupplierImportRowError {
  rowNumber: number;
  reason: string;
  columnName?: string;
}

export interface SupplierImportResult {
  dryRun: boolean;
  cleanRows: number;
  errorRows: SupplierImportRowError[];
  importedRows: number;
  previewRows: SupplierImportPreviewRow[];
}

export interface CsvImportRequestBody {
  headers: string[];
  rows: Record<string, string>[];
}

export async function downloadSuppliersExport(): Promise<void> {
  await apiDownload('/api/suppliers/export', 'suppliers_export.csv');
}

export async function downloadSuppliersTemplate(): Promise<void> {
  await apiDownload('/api/suppliers/import/template', 'suppliers_import.xlsx');
}

export async function previewSuppliersImport(
  body: CsvImportRequestBody,
): Promise<SupplierImportResult> {
  return apiFetch<SupplierImportResult>('/api/suppliers/import/preview', {
    method: 'POST',
    body,
  });
}

export async function confirmSuppliersImport(
  body: CsvImportRequestBody,
): Promise<SupplierImportResult> {
  return apiFetch<SupplierImportResult>('/api/suppliers/import/confirm', {
    method: 'POST',
    body,
  });
}

/* The certification toolkit's Supplier Visit Report (ISO 9001 8.4). */
export interface SupplierVisitPublic {
  id: string;
  supplierId: string;
  supplierName: string;
  visitDate: string;
  department: string | null;
  site: string | null;
  peopleSeen: string;
  productsSupplied: string | null;
  scores: SupplierVisitScores;
  total: number;
  outOf: number;
  meetingSummary: string;
  assessmentNotes: string | null;
  improvementTargets: string | null;
  supplierApproved: boolean;
  completedByName: string | null;
  createdAt: string;
}

export interface RecordSupplierVisitInput {
  visitDate: string;
  department: string | null;
  site: string | null;
  peopleSeen: string;
  productsSupplied: string | null;
  scores: SupplierVisitScores;
  meetingSummary: string;
  assessmentNotes: string | null;
  improvementTargets: string | null;
  supplierApproved: boolean;
}

export interface SupplierQuestionnairePublic {
  id: string;
  supplierId: string;
  supplierName: string;
  status: 'sent' | 'returned' | 'no_email' | 'send_failed';
  sentTo: string | null;
  sendError: string | null;
  sentAt: string | null;
  returnedAt: string | null;
  answers: SupplierAnswers | null;
  supplierDetails: unknown;
  signedName: string | null;
  signedPosition: string | null;
  enteredBy: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export async function fetchSupplierQuestionnaires(supplierId: string, signal?: AbortSignal): Promise<SupplierQuestionnairePublic[]> {
  return apiFetch<SupplierQuestionnairePublic[]>(`/api/suppliers/${supplierId}/questionnaires`, { method: 'GET', signal });
}

export async function sendSupplierQuestionnaire(supplierId: string): Promise<SupplierQuestionnairePublic> {
  return apiFetch<SupplierQuestionnairePublic>(`/api/suppliers/${supplierId}/questionnaires/send`, { method: 'POST', body: {} });
}

export async function enterSupplierQuestionnaire(
  supplierId: string,
  input: { answers: SupplierAnswers; signedName: string; signedPosition: string },
): Promise<SupplierQuestionnairePublic> {
  return apiFetch<SupplierQuestionnairePublic>(`/api/suppliers/${supplierId}/questionnaires/enter`, { method: 'POST', body: input });
}

export async function reviewSupplierQuestionnaire(id: string): Promise<SupplierQuestionnairePublic> {
  return apiFetch<SupplierQuestionnairePublic>(`/api/suppliers/questionnaires/${id}/review`, { method: 'POST', body: {} });
}

export async function fetchSupplierVisits(signal?: AbortSignal): Promise<SupplierVisitPublic[]> {
  return apiFetch<SupplierVisitPublic[]>('/api/suppliers/visits', { method: 'GET', signal });
}

export async function recordSupplierVisit(supplierId: string, input: RecordSupplierVisitInput): Promise<SupplierVisitPublic> {
  return apiFetch<SupplierVisitPublic>(`/api/suppliers/${supplierId}/visits`, { method: 'POST', body: input });
}
